import { useCallback, useEffect, useRef, useState } from 'react';
import RestaurantDashboardService from '../../services/restaurant/RestaurantDashboardService';
import { RestaurantDashboard } from '../../types/restaurant/RestaurantDashboardTypes';

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.message ||
  'No se pudo actualizar el dashboard.';

export const useRestaurantDashboard = (branchId: number | '', date: string) => {
  const [data, setData] = useState<RestaurantDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!branchId) return;
    const currentRequest = ++requestId.current;
    setError(null);
    data ? setRefreshing(true) : setLoading(true);
    try {
      const response = await RestaurantDashboardService.get(branchId, date);
      if (currentRequest === requestId.current) setData(response.data);
    } catch (caught) {
      if (currentRequest === requestId.current) setError(errorMessage(caught));
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [branchId, date, data]);

  useEffect(() => {
    void load();
    // load intentionally follows branch/date; retaining data avoids layout flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, date]);

  useEffect(() => {
    if (!branchId) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [branchId, load]);

  return { data, loading, refreshing, error, refresh: load };
};

export default useRestaurantDashboard;
